<template>
    <div
        data-testid="post-photo-upload"
        class="flex overflow-auto"
    >
        <div
            class="pointer-events-none flex-shrink-1 flex-grow-1 flex-basis-none w-full"
            :style="{ maxWidth: spacerLeftWidth }"
        ></div>
        <Dropzone
            v-show="showDropzone"
            ref="dropzone"
            class="mr-4"
            use-custom-slot
            :include-styling="false"
            :options="dropzoneOptions"
            @vdropzone-file-added="onAdded"
            @vdropzone-success="onSuccess"
            @vdropzone-error="onError"
            @vdropzone-complete="onComplete"
        >
            <div class="relative bg-slate-300 h-20 w-20 p-6 rounded text-slate-400 hover:text-slate-600 cursor-pointer">
                <span class="icon icon-add-circled h-8 w-8"></span>
            </div>
        </Dropzone>
        <div
            v-for="(file, index) in localFiles"
            :key="file.name"
            :class="['flex-1 flex-none relative h-20 w-20', index && 'ml-4']"
        >
            <span
                class="absolute -top-3 -right-3 bg-white h-6 w-6 rounded-full cursor-pointer text-black hover:text-slate-600"
                @click.prevent.stop="removeFile(index)"
            >
                <i class="icon icon-cancel-circled"></i>
            </span>
            <div class="w-full h-full overflow-hidden flex items-center rounded">
                <AssetCdnImage
                    :src="file.is_infected ? $t('assets.placeholder.infectedFile') : (file.originalUrl ?? file.dataURL)"
                    :width="80"
                    :height="80"
                    layout="fixed"
                    object-fit="cover"
                />
            </div>
        </div>
        <Spinner
            v-if="addedFilesCounter"
            :class="['flex flex-shrink-0 justify-center items-center w-20 h-20', files.length && 'ml-4']"
        />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import Dropzone from '@/components/Dropzone';
import { isString } from 'lodash-es';
import route from '@/router/route';

export const EVENTS = {
    UPLOAD_START: 'upload-start',
    UPLOADED: 'uploaded',
    UPLOAD_END: 'upload-end',
    DELETE: 'delete',
};

export const MAX_NUM_FILES = 5;

/**
 * The upload logic was inspected in May 2022.
 * Next inspection due in 2024 :P
 */
export default {
    name: 'PostPhotoUpload',

    components: {
        Dropzone,
    },

    props: {
        files: {
            type: Array,
            default: () => [],
        },
        spacerLeftWidth: {
            type: String,
            default: '0px',
        },
    },
    emits: Object.values(EVENTS),

    i18nOptions: {
        namespaces: ['post'],
    },

    data() {
        return {
            localFiles: [...this.files],
            addedFilesCounter: 0,
        };
    },

    computed: {
        dropzoneOptions() {
            return {
                url: route('post.upload.store'),
                acceptedFiles: 'image/x-png,image/png,image/jpeg,image/jpg,.jpg,.jpeg,.png',
                paramName: 'qqfile',
                previewTemplate: '',
                thumbnail() {
                    // Overwrite to not show thumbnail
                },
                addedfile() {
                    // Overwrite to not show thumbnail
                },
            };
        },
        showDropzone() {
            return this.files.length + this.addedFilesCounter < MAX_NUM_FILES;
        },
    },

    watch: {
        addedFilesCounter(cnt) {
            this.$emit(cnt > 0 ? EVENTS.UPLOAD_START : EVENTS.UPLOAD_END);
        },
    },

    methods: {
        ...mapActions('flashMessages', ['showError']),
        onAdded(file) {
            if (this.files.length + this.addedFilesCounter >= MAX_NUM_FILES) {
                this.$refs.dropzone.call('removeFile', file);
                this.showFilelimitError();
                return;
            }

            this.addedFilesCounter += 1;
        },
        onSuccess(file, response) {
            this.files.push(response.data.file); // eslint-disable-line
            this.localFiles.push(file);

            this.$emit(EVENTS.UPLOADED, response.data.file);
        },
        async onError(file, response) {
            this.showError({
                content: isString(response) ? response : response.error,
                key: new Date().getTime(),
            });
        },
        showFilelimitError() {
            this.showError({
                content: this.$t('post:maxFiles', { maxFiles: MAX_NUM_FILES }),
                key: 'max-files-exceeded',
            });
        },
        onComplete(file) {
            this.$refs.dropzone.call('removeFile', file);
            this.addedFilesCounter -= 1;
        },
        removeFile(index) {
            this.$emit(EVENTS.DELETE, this.files[index], index);

            this.files.splice(index, 1); // eslint-disable-line
            this.localFiles.splice(index, 1);
        },
        triggerFileSelection() {
            this.$refs.dropzone.invoke();
        },
    },
};
</script>
